import { motion } from 'motion/react'
import { ReactNode } from 'react'

interface AnimatedPageProps {
  children: ReactNode
  className?: string
}

// Page transition variants - smooth fade in and slide up
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  }
}

/**
 * AnimatedPage - Wraps page content with smooth entry/exit transitions
 *
 * Use this at the top level of each route/page component to create
 * smooth transitions when navigating between pages.
 *
 * @example
 * ```tsx
 * export function MyPage() {
 *   return (
 *     <AnimatedPage>
 *       <h1>My Page Content</h1>
 *     </AnimatedPage>
 *   )
 * }
 * ```
 */
export function AnimatedPage({ children, className }: AnimatedPageProps) {
  return (
    <motion.div
      className={className}
      initial="initial"
      animate="enter"
      exit="exit"
      variants={pageVariants}
    >
      {children}
    </motion.div>
  )
}
